// Shared UI sound effects — all synthesized via Web Audio API (no audio files)
//
// Normal sounds (coin, chime, click) create a fresh AudioContext per call —
// they are always called from user-gesture handlers so the context starts running.
//
// Warning sounds (buzz, alarm) can fire from useEffect (no gesture). They reuse
// a pre-warmed singleton context that was unlocked by an earlier user-gesture sound.

function getAudioContext(): AudioContext | null {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    return AudioCtx ? new AudioCtx() : null;
  } catch {
    return null;
  }
}

// --- Pre-warmed singleton for warning sounds (may fire outside user gesture) ---
let _warningCtx: AudioContext | null = null;

function warmWarningContext() {
  try {
    if (!_warningCtx || _warningCtx.state === "closed") {
      _warningCtx = getAudioContext();
    }
    if (_warningCtx && _warningCtx.state === "suspended") {
      _warningCtx.resume().catch(() => {});
    }
  } catch { /* ignore */ }
}

function getWarningContext(): AudioContext | null {
  warmWarningContext();
  return _warningCtx;
}

/** Piggy-bank coin drop — plays the real recorded sound file */
export function playCoinReceived() {
  warmWarningContext();
  try {
    const audio = new Audio("/sounds/coin-received.mp3");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch {
    // Audio not available
  }
}

/** Original two-note chime (C6 → E6) — plays when money is sent out */
export function playCoinSent() {
  warmWarningContext();
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const notes = [1046.5, 1318.5]; // C6, E6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      const t = now + i * 0.12;
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.12, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      osc.start(t);
      osc.stop(t + 0.5);
    });

    setTimeout(() => ctx.close(), 800);
  } catch {
    // Audio not available
  }
}

/** Short confirmation chime — plays when executing an action (save, confirm, submit) */
export function playActionConfirm() {
  warmWarningContext();
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    // Two-note ascending interval: A5 → C#6
    const notes = [
      { freq: 880, delay: 0 },
      { freq: 1100, delay: 0.08 },
    ];

    for (const { freq, delay } of notes) {
      const t = now + delay;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.08, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.start(t);
      osc.stop(t + 0.2);
    }

    setTimeout(() => ctx.close(), 450);
  } catch {
    // Audio not available
  }
}

/** Warning buzz — short vibrating tone for match/possible-duplicate warnings (yellow overlay).
 *  Uses pre-warmed context so it works from useEffect. */
export function playWarningBuzz() {
  try {
    const ctx = getWarningContext();
    if (!ctx || ctx.state !== "running") return;
    const now = ctx.currentTime;

    // Two rapid low buzzes with a sawtooth wave
    for (let i = 0; i < 2; i++) {
      const t = now + i * 0.22;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(220, t);
      osc.frequency.linearRampToValueAtTime(180, t + 0.15);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.12, t + 0.01);
      gain.gain.setValueAtTime(0.12, t + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.start(t);
      osc.stop(t + 0.18);
    }
    // Don't close — kept alive for future warning sounds
  } catch {
    // Audio not available
  }
}

/** Danger alarm — harsher triple-pulse for duplicate receipt warnings (red overlay).
 *  Uses pre-warmed context so it works from useEffect. */
export function playDangerAlarm() {
  try {
    const ctx = getWarningContext();
    if (!ctx || ctx.state !== "running") return;
    const now = ctx.currentTime;

    // Three urgent pulses, descending pitch
    const pulses = [
      { freq: 440, delay: 0 },
      { freq: 400, delay: 0.18 },
      { freq: 360, delay: 0.36 },
    ];

    for (const { freq, delay } of pulses) {
      const t = now + delay;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "square";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.09, t + 0.008);
      gain.gain.setValueAtTime(0.09, t + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
      osc.start(t);
      osc.stop(t + 0.14);
    }
    // Don't close — kept alive for future warning sounds
  } catch {
    // Audio not available
  }
}

/** Dampened low thud — felt more than heard */
export function playButtonClick() {
  warmWarningContext();
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(150, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    osc.start(now);
    osc.stop(now + 0.06);

    setTimeout(() => ctx.close(), 150);
  } catch {
    // Audio not available
  }
}
